<template>
    <div class="d-flex flex-fill flex-column" style="padding: 16px;">
        <div class="d-flex">
            <fe-title-select
                v-if="automationInstances.length > 0 && selectedInstance"
                placeholder="Select an Automation Instance"
                :items="automationInstances"
                v-model="selectedInstance"
                itemText="displayName"
                itemValue="name"
            ></fe-title-select>
        </div>

        <fe-dialog
            v-if="lastResultDialog.show"
            :title="lastResultDialog.title"
            :footer="false"
            width="600"
            @close="lastResultDialog.show = false"
        >
            <div style="white-space: pre-wrap; font-family: monospace; font-size: 12px; max-height: 400px; overflow-y: auto; padding: 8px;">{{ lastResultDialog.content }}</div>
        </fe-dialog>

        <fe-dialog
            v-if="passwordDialog.show"
            :title="passwordDialog.title"
            width="600"
            @dismiss="passwordDialog.show = false"
            @accept="savePasswordDialog"
        >
            <div style="padding: 8px;">
                <v-checkbox
                    v-if="passwordDialog.showUseDefault"
                    v-model="passwordDialog.useDefault"
                    data-test="automation-password-dialog-use-default"
                    label="Use default key"
                    hide-details
                    class="mb-2"
                    @change="onUseDefaultKeyChanged"
                ></v-checkbox>
                <textarea
                    v-model="passwordDialog.value"
                    :aria-label="passwordDialog.title"
                    :placeholder="passwordDialog.placeholder"
                    :disabled="passwordDialog.useDefault"
                    maxlength="10000"
                    style="width: 100%; height: 200px; font-family: monospace; font-size: 12px; padding: 8px; border: 1px solid #ccc; border-radius: 4px; resize: vertical;"
                    :style="{ backgroundColor: passwordDialog.useDefault ? '#f5f5f5' : '#fff', color: passwordDialog.useDefault ? '#999' : 'inherit' }"
                ></textarea>
                <div v-if="passwordDialog.hint && !passwordDialog.useDefault" style="margin-top: 6px; font-size: 11px; color: #666;">{{ passwordDialog.hint }}</div>
            </div>
            <template v-slot:footer>
                <v-spacer />
                <fe-btn usage="ghost" data-test="automation-password-dialog-cancel" @click="passwordDialog.show = false">Cancel</fe-btn>
                <fe-btn data-test="automation-password-dialog-save" @click="savePasswordDialog">Save</fe-btn>
            </template>
        </fe-dialog>

        <fe-crud-grid
            v-if="gridReady"
            :key="gridRenderKey"
            ref="grid"
            :columnDefs="columnDefs"
            :config="$models.instanceAutomation"
            :createParams="instanceParams"
            :readParams="instanceParams"
            :updateParams="instanceParams"
            :destroyParams="instanceParams"
            :insertDefaults="insertDefaults"
            refreshOnCreated
            refreshOnUpdated
            style="height: 100%"
            displayDensity="small"
        />
    </div>
</template>

<script>
    // in order to add an info icon while maintaining the ability to sort, filter, and menu, we need to use the simple header template
    // https://www.ag-grid.com/archive/25.1.0/javascript-grid/column-headers/#example-simple-header-templates
    const headerWithTooltipIconTemplate = `
        <div class="ag-cell-label-container" role="presentation">
            <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
            <div ref="eLabel" class="ag-header-cell-label" role="presentation">
                <span ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>
                <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>
                <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>
                <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>
                <span ref="eText" class="ag-header-cell-text" role="columnheader"></span>
                <i class="fal fa-info-circle" style="color: #495B8F;"></i>
                <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
            </div>
        </div>
    `

    export default {
        name: 'AimswebAutomation',
        data() {
            return {
                gridReady: false,
                gridRenderKey: `grid-${Math.random()}`,
                automationInstances: [],
                selectedInstance: null,
                lastResultDialog: {
                    show: false,
                    title: '',
                    content: '',
                },
                passwordDialog: {
                    show: false,
                    title: '',
                    placeholder: '',
                    hint: '',
                    value: '',
                    useDefault: false,
                    showUseDefault: false,
                    prop: null,
                    rowNode: null,
                    api: null,
                },
            }
        },
        async mounted() {
            this.automationInstances = await this.$modelGet('automationInstance').then(res => res.map(itm => ({
                ...itm,
                displayName: `${itm.name} Automation Manager`,
            })))

            if (this.automationInstances?.length > 0) {
                this.selectedInstance = this.automationInstances[0]
            }


            // Wait for nextTick to allow computed instanceParams to propagate
            this.$nextTick(() => {
                this.gridReady = true
            })
        },
        computed: {
            instanceParams() {
                return {
                    property: this.selectedInstance?.property.toLowerCase(),
                }
            },
            insertDefaults() {
                const defaults = {}
                
                if (!this.selectedInstance?.props) return defaults

                this.selectedInstance.props.forEach(prop => {
                    if (this.isToggleColumn(prop)) {
                        defaults[prop.column] = this.getToggleDefaultValue(prop)
                    }
                })
                
                return defaults
            },
            columnDefs() {
                let columnDefs = [
                    {
                        ...this.$grid.checkColumn(),
                        headerName: 'Checkbox',
                        colId: 'checkbox_column',
                        field: 'checkbox_column',
                        width: 50,
                        minWidth: 50,
                        maxWidth: 50,
                        pinned: 'left',
                    },
                    {
                        colId: 'id',
                        headerName: 'ID',
                        field: 'id',
                        width: 50,
                        minWidth: 50,
                        maxWidth: 50,
                        pinned: 'left',
                        hide: true,
                    },
                ]

                const readOnlyStyle = {
                    backgroundColor: '#f5f5f5',
                    color: '#666',
                    fontStyle: 'italic'
                }

                // Show relevant messaging associated with success or failure
                const decorateReadonlyCellWithErrorHandling = (columnDef, successFunction, errorMessage) => {
                    // Content is colorable if the row is active and the value is not null or empty
                    const colorableContent = (data, value) => (data?.active && !!value)
                    columnDef.cellRenderer = ({data, value}) => {
                        if (!colorableContent(data,value)) {
                            return value || ''
                        }

                        return successFunction(value) ? value : `<i class="far fa-exclamation-circle" style="color: #BF0D00; margin-right: 5px;"></i>${value}`
                    }
                    columnDef.cellStyle = ({data, value}) => {
                        // Show readonly if content can't be displayed, row isn't active, or success function returns true
                        return !colorableContent(data, value) || successFunction(value) ? readOnlyStyle : {
                            backgroundColor: '#F8EEED',
                            color: '#BF0D00'
                        }
                    }
                    // otherwise show error style
                    if (errorMessage) {
                        columnDef.tooltipValueGetter = (params) => {
                            return successFunction(params.value) ? "" : errorMessage
                        }
                    }
                }

                for (let prop of this.selectedInstance?.props) {
                    let columnDef
                    if (prop.column == 'active' || prop.approval_toggle || prop.basic_toggle) {
                        columnDef = {
                            colId: prop.column,
                            headerName: prop.name,
                            field: prop.column,
                            cellRendererFramework: 'FeGridToggle',
                            minWidth: 60,
                            width: 80,
                        }
                        
                        // Add tooltip to column header if provided by backend
                        if (prop.header_tooltip) {
                            columnDef.headerTooltip = prop.header_tooltip
                            columnDef.headerComponentParams = {template: headerWithTooltipIconTemplate}
                        }

                        // For approval toggle columns, make them conditionally editable
                        if (prop.approval_toggle) {
                            // Force display value to false when conditions aren't met
                            columnDef.valueGetter = (params) => {
                                if (!this.canEditApprovalToggle(params.data)) {
                                    return false
                                }
                                return params.data[prop.column]
                            }
                            
                            columnDef.cellStyle = (params) => {
                                const canEdit = params.data?.local_dir && this.canEditApprovalToggle(params.data)
                                return {
                                    backgroundColor: canEdit ? '' : '#f5f5f5',
                                    pointerEvents: canEdit ? 'auto' : 'none',
                                    contentVisibility: params.data?.local_dir ? 'auto' : 'hidden',
                                }
                            }
                            
                            columnDef.editable = (params) => 
                                this.canEditApprovalToggle(params.data)
                        }
                        
                    } else {
                        columnDef = {
                            colId: prop.column,
                            headerName: prop.name,
                            field: prop.column,
                            width: prop.width || 100,
                            cellStyle: { color: '#006C90' }, // Blue
                            editable: !prop.read_only, // Make editable unless marked as read_only
                        }

                        // Add tooltip to column header if provided by backend
                        if (prop.header_tooltip) {
                            columnDef.headerTooltip = prop.header_tooltip
                            columnDef.headerComponentParams = {template: headerWithTooltipIconTemplate}
                        }

                        // Add validation rules if provided by backend
                        if (prop.validation_regex || prop.min_length || prop.max_length) {
                            columnDef.cellEditorParams = columnDef.cellEditorParams || {}
                            columnDef.valueSetter = (params) => 
                                this.validateAndSetCellValue(params, prop)
                        }
                        if (prop.approval_required_field || prop.column === 'local_dir') {
                            const existingValueSetter = columnDef.valueSetter
                            columnDef.valueSetter = (params) => {
                                let valid = false
                                if (!existingValueSetter) {
                                    params.data[prop.column] = params.newValue
                                    valid = true
                                } else {
                                    valid = existingValueSetter(params)
                                }
                                this.$nextTick(() => {
                                    params.api.refreshCells({
                                        rowNodes: [params.node],
                                        columns: ['approved'],
                                        force: true,
                                    })
                                })
                                return valid
                            }
                        }

                        if (prop.is_password) {
                            columnDef.cellRenderer = v => v.value ? '&bull;'.repeat(16) : ''
                        }

                        if (prop.is_multiline) {
                            const defaultKeySentinel = prop.default_key_sentinel
                            const supportsDefaultKey = typeof defaultKeySentinel === 'string' && defaultKeySentinel !== ''
                            columnDef.cellRenderer = v => {
                                let content
                                if (supportsDefaultKey && v.value === defaultKeySentinel) {
                                    content = '<span style="color: #666; font-style: italic;">Default key</span>'
                                } else if (v.value) {
                                    content = '&bull;'.repeat(16)
                                } else {
                                    content = '<span style="color: #999; font-style: italic;">Click to set</span>'
                                }
                                return `<span data-test="automation-multiline-cell-${prop.column}">${content}</span>`
                            }
                            columnDef.editable = false
                            if (supportsDefaultKey) {
                                columnDef.tooltipValueGetter = params => {
                                    if (params.value === defaultKeySentinel) {
                                        return 'Authenticates with the shared default key, not a district-specific one'
                                    }
                                    if (params.value) {
                                        return `Using a district-specific ${prop.name}. Clear it and check "Use default key" to use the shared one instead.`
                                    }
                                    return `Set a ${prop.name}, or check "Use default key" to use the shared one`
                                }
                            }
                            columnDef.onCellClicked = ({ node, api }) => {
                                const storedValue = node.data[prop.column] || ''
                                const usingDefaultKey = supportsDefaultKey && storedValue === defaultKeySentinel
                                this.passwordDialog = {
                                    show: true,
                                    title: `Set ${prop.name}`,
                                    placeholder: `Paste ${prop.name} here...`,
                                    hint: prop.validation_message || '',
                                    value: usingDefaultKey ? '' : storedValue,
                                    useDefault: usingDefaultKey,
                                    showUseDefault: supportsDefaultKey,
                                    prop,
                                    rowNode: node,
                                    api,
                                }
                            }
                        }
                        
                        if (prop.read_only) {
                            columnDef.cellStyle = readOnlyStyle
                        } 

                        if (prop.is_response_code_field) {
                            decorateReadonlyCellWithErrorHandling(columnDef,(code) => {
                                const parsedCode = parseInt(code)
                                return parsedCode >= 200 && parsedCode < 300 // 200-299 is success
                            },
                            "API request failed. Check automation service status.")
                        }

                        if (prop.is_date_sync_field) {
                            const maxHours = 24
                            decorateReadonlyCellWithErrorHandling(columnDef,(date) => 
                                new Date(date) > new Date(Date.now() - maxHours * 60 * 60 * 1000) // Less than 24 hours ago is success
                            ,
                            `The last successful authentication was more than ${maxHours} hours ago.`)
                        }

                        if (prop.column === 'last_result') {
                            columnDef.cellRenderer = ({value}) => {
                                if (!value) return ''
                                const lines = value.trim().split('\n')
                                const lastLine = lines[lines.length - 1] || value
                                return `<span style="cursor: pointer; text-decoration: underline; color: #006C90;">${lastLine}</span>`
                            }
                            columnDef.onCellClicked = ({value}) => {
                                if (!value) return
                                this.lastResultDialog = {
                                    show: true,
                                    title: 'Last Result',
                                    content: value,
                                }
                            }
                        }

                        if (prop.min_approval_value !== undefined && prop.min_approval_value !== null) {
                            decorateReadonlyCellWithErrorHandling(columnDef, (value) => {
                                const numValue = parseFloat(value)
                                return !isNaN(numValue) && numValue >= prop.min_approval_value
                            },
                            `Value must be at least ${prop.min_approval_value} for approval.`)
                        }

                        if (prop.column === 'state_student_id_match_percentage') {
                            columnDef.comparator = (num1, num2) => {
                                return parseFloat(num1) - parseFloat(num2)
                            }
                        }
                    }

                    columnDefs.push(columnDef)
                }

                columnDefs = [
                    ...columnDefs,
                    {
                        colId: 'created',
                        headerName: 'Created',
                        field: 'created',
                        minWidth: 150,
                        maxWidth: 150,
                        editable: false,
                        cellStyle: readOnlyStyle
                    }, {
                        colId: 'updated',
                        headerName: 'Updated',
                        field: 'updated',
                        minWidth: 150,
                        maxWidth: 150,
                        editable: false,
                        cellStyle: readOnlyStyle
                    },
                ]

                return columnDefs
            },
        },
        methods: {
            // === Toggle Column Helpers ===
            isToggleColumn(prop) {
                return prop.column === 'active' || prop.approval_toggle || prop.basic_toggle
            },

            getToggleDefaultValue(prop) {
                if (prop.default_value !== undefined) {
                    return prop.default_value
                }
                return prop.column === 'active' ? 1 : 0
            },

            canEditApprovalToggle(rowData) {
                return this.isRowActive(rowData) && 
                       this.allRequiredFieldsHaveValues(rowData) &&
                       this.allMinApprovalsValid(rowData)
            },

            isRowActive(rowData) {
                const active = rowData.active
                return active === 1 || active === true || active === '1'
            },

            allRequiredFieldsHaveValues(rowData) {
                const requiredFields = this.selectedInstance?.props?.filter(p => 
                    p.approval_required_field === true
                )
                
                if (!requiredFields || requiredFields.length === 0) {
                    return true
                }

                return requiredFields.every(field => {
                    const value = rowData[field.column]
                    return value !== null && 
                           value !== undefined && 
                           value !== '' &&
                           value !== false
                })
            },

            allMinApprovalsValid(rowData) {
                const minApprovalFields = this.selectedInstance?.props?.filter(p => 
                    p.min_approval_value !== undefined && p.min_approval_value !== null
                )
                
                if (!minApprovalFields || minApprovalFields.length === 0) {
                    return true
                }

                return minApprovalFields.every(field => {
                    const value = rowData[field.column]
                    const numValue = parseFloat(value)
                    return !isNaN(numValue) && numValue >= field.min_approval_value
                })
            },

            // === Field Validation ===
            validateAndSetCellValue(params, prop) {
                const validators = [this.validateMinLength, this.validateMaxLength, this.validateRegex]
                const isValid = validators.every(fn => fn.call(this, params.newValue, prop))
                
                if (isValid) params.data[prop.column] = params.newValue
                return isValid
            },

            validateMinLength(value, prop) {
                if (!prop.min_length || !value || value.length >= prop.min_length) return true
                this.showValidationError(prop.validation_message || `${prop.name} must be at least ${prop.min_length} characters`)
                return false
            },

            validateMaxLength(value, prop) {
                if (!prop.max_length || !value || value.length <= prop.max_length) return true
                this.showValidationError(prop.validation_message || `${prop.name} must be at most ${prop.max_length} characters`)
                return false
            },

            validateRegex(value, prop) {
                if (!prop.validation_regex || !value) return true
                try {
                    if (new RegExp(prop.validation_regex).test(value)) return true
                    this.showValidationError(prop.validation_message || `${prop.name} has invalid format`)
                    return false
                } catch (e) {
                    console.error('Invalid regex pattern:', prop.validation_regex, e)
                    return true
                }
            },

            onUseDefaultKeyChanged(checked) {
                if (checked) {
                    this.passwordDialog.value = ''
                }
            },

            savePasswordDialog() {
                const { value, useDefault, prop, rowNode, api } = this.passwordDialog

                if (!useDefault) {
                    if (this.passwordDialog.showUseDefault && !value) {
                        this.showValidationError(`Paste a ${prop.name}, or check "Use default key"`)
                        return
                    }

                    const validators = [this.validateMinLength, this.validateMaxLength, this.validateRegex]
                    const isValid = validators.every(fn => fn.call(this, value, prop))

                    if (!isValid) return
                }

                const newValue = useDefault ? prop.default_key_sentinel : value
                const oldValue = rowNode.data[prop.column]
                rowNode.data[prop.column] = newValue

                api.dispatchEvent({
                    type: 'cellValueChanged',
                    node: rowNode,
                    data: rowNode.data,
                    oldValue,
                    newValue,
                    colDef: { field: prop.column },
                    api,
                })

                api.refreshCells({ rowNodes: [rowNode], columns: [prop.column], force: true })

                if (prop.is_material_field) {
                    this.$nextTick(() => {
                        api.refreshCells({ rowNodes: [rowNode], columns: ['approved'], force: true })
                    })
                }

                this.passwordDialog.show = false
            },

            showValidationError(message) {
                this.$snackbars.$emit('new', {
                    text: 'Validation Error',
                    subtext: message,
                    usage: 'error'
                })
            },
        },
        watch: {
            selectedInstance(v) {
                // When we whole-sale change columnDefs on an ag grid, it gets
                // confused and starts screwing up the order of columns.  Let's
                // change the vue render key on the whole thing to force a full re-render
                this.gridRenderKey = `grid-${Math.random()}`

                // Wait for nextTick to allow computed instanceParams to propagate
                this.$nextTick(() => {
                    this.$refs.grid?.$refs.crud.read()
                })
            }
        },
    }
</script>

<style lang="scss" scoped>

</style>


